import { HEADING_REGEX, IMAGE_REGEX, LINK_REGEX, URL_REGEX } from './constants';

export const countAsterisks = (text: string, fromStart: boolean): number => {
  let count = 0;
  const length = text.length;
  for (let i = fromStart ? 0 : length - 1; fromStart ? i < length : i >= 0; fromStart ? i++ : i--) {
    if (text[i] === '*') count++;
    else break;
  }
  return count;
};

export const getAsteriskCount = (text: string): number => {
  const startAsteriskCount = countAsterisks(text, true);
  const endAsteriskCount = countAsterisks(text, false);
  return Math.min(startAsteriskCount, endAsteriskCount);
};

export const isBold = (asteriskCount: number): boolean => {
  return asteriskCount === 2 || asteriskCount === 3;
};

export const isItalic = (asteriskCount: number): boolean => {
  return asteriskCount === 1 || asteriskCount === 3;
};

export const getListTypes = (text: string): { isBulletList: boolean; isOrderedList: boolean } => {
  const lines = text.split('\n');
  const isBulletList = lines.every(line => /^[-*]\s/.test(line));
  const isOrderedList = lines.every(line => /^\d+\.\s/.test(line));
  return { isBulletList, isOrderedList };
};

export const getMarkdownUrlType = (selectedText: string): 'image' | 'markdown-link' | 'plain-url' | 'none' => {
  if (IMAGE_REGEX.test(selectedText)) {
    return 'image';
  } else if (LINK_REGEX.test(selectedText)) {
    return 'markdown-link';
  } else if (URL_REGEX.test(selectedText)) {
    return 'plain-url';
  } else {
    return 'none';
  }
};

export const getLinkAndImageTypes = (text: string): { isLink: boolean; isImage: boolean } => {
  const isLink = getMarkdownUrlType(text) === 'markdown-link' || getMarkdownUrlType(text) === 'plain-url';
  const isImage = getMarkdownUrlType(text) === 'image';
  return { isLink, isImage };
};

export const getMarkdownHeadingLevel = (selectedText: string): number => {
  const match = selectedText.match(HEADING_REGEX);
  // return 0 for normal text or the number of # symbols for heading level
  return match ? match[1].length : 0;
};

export const calculateNewMarkerCounts = (
  marker: string,
  leadingCount: number,
  trailingCount: number
): { newLeadingCount: number; newTrailingCount: number } => {
  // Validate inputs
  if (leadingCount < 0 || trailingCount < 0) {
    throw new Error('Leading and trailing counts must be non-negative integers.');
  }

  let newLeadingCount = leadingCount;
  let newTrailingCount = trailingCount;

  if (marker === '*') {
    // Handle italic formatting
    newLeadingCount = leadingCount % 2 === 1 ? leadingCount - 1 : leadingCount + 1;
    newTrailingCount = trailingCount % 2 === 1 ? trailingCount - 1 : trailingCount + 1;
  } else if (marker === '**') {
    // Handle bold formatting
    newLeadingCount = leadingCount >= 2 ? leadingCount - 2 : leadingCount + 2;
    newTrailingCount = trailingCount >= 2 ? trailingCount - 2 : trailingCount + 2;
  } else {
    throw new Error('Invalid marker type. Only "*" and "**" are supported.');
  }

  // Prevent negative values (e.g., from subtracting too much)
  newLeadingCount = Math.max(0, newLeadingCount);
  newTrailingCount = Math.max(0, newTrailingCount);

  return { newLeadingCount, newTrailingCount };
};

export const constructFormattedText = (
  unformattedText: string,
  leadingCount: number,
  trailingCount: number
): string => {
  const leadingMarkers = '*'.repeat(leadingCount);
  const trailingMarkers = '*'.repeat(trailingCount);
  return `${leadingMarkers}${unformattedText}${trailingMarkers}`;
};

export const extractFormatting = (
  text: string
): { leadingCount: number; trailingCount: number; unformattedText: string } => {
  let leadingCount = 0;
  let trailingCount = 0;

  // Count leading markers
  while (text[leadingCount] === '*') {
    leadingCount++;
  }

  // Count trailing markers
  while (text[text.length - 1 - trailingCount] === '*') {
    trailingCount++;
  }

  // Extract unformatted text
  const unformattedText = text.slice(leadingCount, text.length - trailingCount);
  return { leadingCount, trailingCount, unformattedText };
};

// Defines the extension for opening links in a new tab
export const openLinksInNewTabExtension = () => {
  return [
    {
      type: 'output',
      regex: /(<a\s+href="[^"]+")/g,
      replace: '$1 target="_blank" rel="noopener noreferrer"',
    },
  ];
};
